
/**
 * Encryption utility for protecting PII in Firestore.
 * Uses Web Crypto API (AES-GCM) which is available in both browser and Expo environments.
 */

const ALGO = 'AES-GCM';
const KEY_LEN = 256;

/**
 * Generates a random 256-bit AES-GCM key (DEK).
 */
export async function generateDEK(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(
        { name: ALGO, length: KEY_LEN },
        true,
        ['encrypt', 'decrypt']
    );
}

/**
 * Exports a CryptoKey to a raw format (Uint8Array).
 */
export async function exportKey(key: CryptoKey): Promise<Uint8Array> {
    const exported = await crypto.subtle.exportKey('raw', key);
    return new Uint8Array(exported);
}

/**
 * Imports a raw key into a CryptoKey object.
 */
export async function importKey(rawKey: Uint8Array): Promise<CryptoKey> {
    return crypto.subtle.importKey(
        'raw',
        rawKey as any,
        ALGO,
        true,
        ['encrypt', 'decrypt']
    );
}

/**
 * Converts a hex string to a Uint8Array.
 */
export function hexToUint8Array(hex: string): Uint8Array {
    const result = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        result[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return result;
}

/**
 * Converts a Uint8Array or ArrayBuffer to a base64 string.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer | ArrayBufferView): string {
    const bytes = ArrayBuffer.isView(buffer)
        ? new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength)
        : new Uint8Array(buffer);

    // Use Buffer if available (Node/Vitest)
    if (typeof Buffer !== 'undefined') {
        return Buffer.from(bytes).toString('base64');
    }

    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * Converts a base64 string to a Uint8Array.
 */
export function base64ToUint8Array(base64: string): Uint8Array {
    // Use Buffer if available (Node/Vitest)
    if (typeof Buffer !== 'undefined') {
        const buf = Buffer.from(base64, 'base64');
        return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
    }

    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

/**
 * Derives a cryptographic key from a password/seed and salt.
 */
async function deriveKey(password: string, salt: any): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const baseKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        'PBKDF2',
        false,
        ['deriveKey']
    );

    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100000,
            hash: 'SHA-256'
        },
        baseKey,
        { name: ALGO, length: KEY_LEN },
        true,
        ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
    );
}

/**
 * Wraps a DEK with a KEK derived from a password.
 * Returns: base64(iv) + ":" + base64(wrappedKey)
 */
export async function wrapKey(dek: CryptoKey, password: string, saltHex: string): Promise<string> {
    const salt = hexToUint8Array(saltHex);
    const kek = await deriveKey(password, salt);
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const wrappedKey = await crypto.subtle.wrapKey(
        'raw',
        dek,
        kek,
        { name: ALGO, iv: iv as any }
    );

    return `${arrayBufferToBase64(iv)}:${arrayBufferToBase64(wrappedKey)}`;
}

/**
 * Unwraps a DEK using a KEK derived from a password.
 */
export async function unwrapKey(wrappedData: string, password: string, saltHex: string): Promise<CryptoKey> {
    const [ivBase64, cipherBase64] = wrappedData.split(':');
    const iv = base64ToUint8Array(ivBase64);
    const wrappedKey = base64ToUint8Array(cipherBase64);
    const salt = hexToUint8Array(saltHex);
    const kek = await deriveKey(password, salt);

    return crypto.subtle.unwrapKey(
        'raw',
        wrappedKey as any,
        kek,
        { name: ALGO, iv: iv as any },
        ALGO,
        true,
        ['encrypt', 'decrypt']
    );
}

/**
 * Encrypts a string value using a CryptoKey or password.
 * Output format: base64(iv) + ":" + base64(ciphertext)
 */
export async function encrypt(text: string, passwordOrKey: string | CryptoKey, saltHex?: string): Promise<string> {
    if (!text) return text;

    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    let key: CryptoKey;

    if (typeof passwordOrKey === 'string') {
        if (!saltHex) throw new Error('saltHex is required for password-based encryption');
        const salt = hexToUint8Array(saltHex);
        key = await deriveKey(passwordOrKey, salt);
    } else {
        key = passwordOrKey;
    }

    const ciphertext = await crypto.subtle.encrypt(
        { name: ALGO, iv: iv as any },
        key,
        encoder.encode(text) as any
    );

    const ivBase64 = arrayBufferToBase64(iv);
    const cipherBase64 = arrayBufferToBase64(ciphertext);

    return `${ivBase64}:${cipherBase64}`;
}

/**
 * Decrypts a base64 encoded string using a CryptoKey or password.
 */
export async function decrypt(encryptedData: string, passwordOrKey: string | CryptoKey, saltHex?: string): Promise<string> {
    if (!encryptedData || !encryptedData.includes(':')) return encryptedData;

    const [ivBase64, cipherBase64] = encryptedData.split(':');
    const iv = base64ToUint8Array(ivBase64);
    const ciphertext = base64ToUint8Array(cipherBase64);
    let key: CryptoKey;

    if (typeof passwordOrKey === 'string') {
        if (!saltHex) throw new Error('saltHex is required for password-based decryption');
        const salt = hexToUint8Array(saltHex);
        key = await deriveKey(passwordOrKey, salt);
    } else {
        key = passwordOrKey;
    }

    const decoder = new TextDecoder();

    const plaintext = await crypto.subtle.decrypt(
        { name: ALGO, iv: iv as any },
        key,
        ciphertext as any
    );

    return decoder.decode(plaintext);
}
